import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotificationCounts, useNotifications, useMarkRead, useMarkAllRead } from '../api/hooks';
import { theme as T } from '../theme';
import type { Notification } from '../api/types';

const BellIcon = () => (
  <svg width={20} height={20} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const priorityColor = (p: Notification['priority']) =>
  ({ urgent: T.danger, high: T.accentOrange, normal: T.accentBlue, low: T.textDim }[p] ?? T.textDim);

function timeAgo(iso: string, lang: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  const pt = lang.startsWith('pt');
  if (diff < 60) return pt ? 'agora' : 'just now';
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return pt ? `${m}min atrás` : `${m}m ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return pt ? `${h}h atrás` : `${h}h ago`;
  }
  const d = Math.floor(diff / 86400);
  return pt ? `${d}d atrás` : `${d}d ago`;
}

function NotificationItem({
  n, onRead,
}: {
  n: Notification;
  onRead: (id: string) => void;
}) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const handleClick = () => {
    if (!n.is_read) onRead(n.id);
    if (n.action_url) {
      if (n.action_url.startsWith('/')) navigate(n.action_url);
      else window.open(n.action_url, '_blank');
    }
  };

  return (
    <div
      onClick={handleClick}
      style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${T.border}`,
        cursor: n.action_url ? 'pointer' : 'default',
        background: n.is_read ? 'transparent' : T.accentGlow,
        transition: 'background 0.15s',
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}
      onMouseEnter={e => { if (n.action_url) e.currentTarget.style.background = T.bgCardHover; }}
      onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? 'transparent' : T.accentGlow; }}
    >
      {/* Priority stripe */}
      <div style={{
        width: 3, borderRadius: 99, alignSelf: 'stretch', flexShrink: 0,
        background: priorityColor(n.priority),
        minHeight: 36,
      }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <span style={{
            fontSize: 13, fontWeight: n.is_read ? 400 : 600,
            color: T.text, lineHeight: 1.4,
          }}>
            {n.icon && <span style={{ marginRight: 6 }}>{n.icon}</span>}
            {n.title}
          </span>
          <span style={{ fontSize: 11, color: T.textDim, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {timeAgo(n.created_at, i18n.language)}
          </span>
        </div>
        <p style={{
          margin: '3px 0 0', fontSize: 12, color: T.textMuted,
          lineHeight: 1.5, overflow: 'hidden',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const,
        }}>
          {n.message}
        </p>
        {n.action_label && (
          <span style={{ marginTop: 6, display: 'inline-block', fontSize: 11, color: T.accent, fontWeight: 600 }}>
            {n.action_label} →
          </span>
        )}
      </div>

      {!n.is_read && (
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.accent, flexShrink: 0, marginTop: 4 }} />
      )}
    </div>
  );
}

export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: counts } = useNotificationCounts();
  const { data: listData } = useNotifications({ page_size: 15, page: 1 });
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const unread = counts?.unread ?? 0;
  const notifications: Notification[] = (listData as any)?.notifications ?? [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkAllRead = () => {
    markAllRead.mutate();
  };

  const handleMarkOne = (id: string) => {
    markRead.mutate([id]);
  };

  const pt = i18n.language?.startsWith('pt');

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: open ? T.accentGlow : 'none',
          border: open ? `1px solid ${T.accent}44` : '1px solid transparent',
          borderRadius: T.radiusSm,
          color: open ? T.accent : T.textMuted,
          cursor: 'pointer', padding: '6px 8px',
          position: 'relative', display: 'flex', alignItems: 'center',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.color = T.text; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.color = T.textMuted; }}
      >
        <BellIcon />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2,
            minWidth: 16, height: 16, borderRadius: 99,
            background: T.danger, border: `2px solid ${T.bgCard}`,
            fontSize: 9, fontWeight: 700, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 3px',
          }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          width: 360, maxHeight: 480,
          background: T.bgCard, border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden', zIndex: 200,
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${T.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
              {pt ? 'Notificações' : 'Notifications'}
              {unread > 0 && (
                <span style={{
                  marginLeft: 8, padding: '1px 7px', borderRadius: 99,
                  background: T.danger + '22', color: T.danger,
                  fontSize: 11, fontWeight: 700,
                }}>
                  {unread} {pt ? 'novas' : 'new'}
                </span>
              )}
            </span>
            {unread > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={markAllRead.isPending}
                style={{
                  background: 'none', border: 'none', fontSize: 12,
                  color: T.accent, cursor: 'pointer', fontWeight: 500,
                  opacity: markAllRead.isPending ? 0.5 : 1,
                }}
              >
                {pt ? 'Marcar tudo como lido' : 'Mark all read'}
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {notifications.length === 0 ? (
              <div style={{
                padding: '40px 20px', textAlign: 'center',
                color: T.textDim, fontSize: 13,
              }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>🔔</div>
                {pt ? 'Nenhuma notificação ainda' : 'No notifications yet'}
              </div>
            ) : (
              notifications.map(n => (
                <NotificationItem key={n.id} n={n} onRead={handleMarkOne} />
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{
              padding: '10px 16px',
              borderTop: `1px solid ${T.border}`,
              textAlign: 'center',
            }}>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: 'none', border: 'none', fontSize: 12,
                  color: T.textDim, cursor: 'pointer',
                }}
              >
                {pt ? 'Fechar' : 'Close'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
